import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { AlertCircle, Loader2, RefreshCcw } from "lucide-react";
import api from "../apiWithAccessToken";
import DashboardLayout from "./DashboardLayout";
import SuperAdminLayout from "./SuperAdmin/SuperAdminLayout";
import {
  clearLegacyUnsafeStorage,
  getStorageScope,
  resetAppStorageForLogout,
  setCurrentStorageScope,
} from "../utils/scopedStorage";
import {
  getPlatformWorkspace,
  isPathAllowedForPlatformWorkspace,
} from "../utils/platformWorkspace";

const LoadingScreen = () => (
  <div
    style={{
      minHeight: "100vh",
      display: "grid",
      placeItems: "center",
      background:
        "radial-gradient(circle at top, rgba(33,163,243,0.12), transparent 30%), var(--background)",
      color: "var(--foreground)",
    }}
  >
    <div
      style={{
        width: "min(420px, calc(100vw - 32px))",
        padding: "28px",
        borderRadius: "24px",
        border: "1px solid var(--border)",
        background: "var(--card)",
        boxShadow: "0 20px 60px rgba(15, 23, 42, 0.12)",
        textAlign: "center",
      }}
    >
      <Loader2
        size={34}
        style={{
          margin: "0 auto 16px",
          color: "var(--primary)",
          animation: "spin 1s linear infinite",
        }}
      />
      <h1 style={{ fontSize: "1.05rem", fontWeight: 700, marginBottom: 8 }}>
        Preparing your workspace
      </h1>
      <p style={{ color: "var(--muted-foreground)", fontSize: "0.92rem" }}>
        Loading the right experience for your account and deployment mode.
      </p>
    </div>
  </div>
);

const ErrorScreen = ({ onRetry }) => (
  <div
    style={{
      minHeight: "100vh",
      display: "grid",
      placeItems: "center",
      background:
        "radial-gradient(circle at top, rgba(228,91,205,0.1), transparent 30%), var(--background)",
      color: "var(--foreground)",
    }}
  >
    <div
      style={{
        width: "min(460px, calc(100vw - 32px))",
        padding: "28px",
        borderRadius: "24px",
        border: "1px solid var(--border)",
        background: "var(--card)",
        boxShadow: "0 20px 60px rgba(15, 23, 42, 0.12)",
      }}
    >
      <div
        style={{
          width: 48,
          height: 48,
          borderRadius: 16,
          display: "grid",
          placeItems: "center",
          marginBottom: 18,
          background: "rgba(220,38,38,0.08)",
          color: "var(--destructive)",
        }}
      >
        <AlertCircle size={24} />
      </div>
      <h1 style={{ fontSize: "1.05rem", fontWeight: 700, marginBottom: 8 }}>
        We couldn&apos;t load your workspace
      </h1>
      <p
        style={{
          color: "var(--muted-foreground)",
          fontSize: "0.92rem",
          lineHeight: 1.6,
          marginBottom: 18,
        }}
      >
        The session is valid, but the profile bootstrap request failed. Retry to
        load the correct UI for your account.
      </p>
      <button
        type="button"
        onClick={onRetry}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          padding: "10px 14px",
          borderRadius: 14,
          border: "1px solid var(--border)",
          background: "var(--surface)",
          color: "var(--foreground)",
          cursor: "pointer",
          fontWeight: 600,
        }}
      >
        <RefreshCcw size={16} />
        Retry
      </button>
    </div>
  </div>
);

const OnPremUnavailableScreen = ({ onRetry }) => (
  <div className="min-h-screen grid place-items-center bg-background text-foreground p-4">
    <div className="w-full max-w-md border border-border bg-card p-7 text-center shadow-lg rounded-lg">
      <AlertCircle size={32} className="mx-auto mb-4 text-destructive" />
      <h1 className="text-lg font-bold">On-prem administration unavailable</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        This session does not currently have access to the on-prem administration workspace.
      </p>
      <div className="mt-5 flex justify-center gap-3">
        <button type="button" className="platform-button" onClick={onRetry}>
          <RefreshCcw size={16} /> Refresh profile
        </button>
        <button
          type="button"
          className="platform-button is-secondary"
          onClick={() => {
            resetAppStorageForLogout();
            window.location.href = "/login";
          }}
        >
          Log out
        </button>
      </div>
    </div>
  </div>
);

export default function AppBootstrap() {
  const location = useLocation();
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({
    status: "loading",
    profile: null,
    storageScope: null,
  });

  useEffect(() => {
    let cancelled = false;

    const loadProfile = async () => {
      setState({ status: "loading", profile: null, storageScope: null });

      try {
        const { data } = await api.get("/auth/me");
        if (cancelled) return;
        clearLegacyUnsafeStorage();
        const scope = getStorageScope(data);
        setCurrentStorageScope(scope);
        setState({ status: "ready", profile: data, storageScope: scope });
      } catch (error) {
        if (cancelled) return;
        setState({ status: "error", profile: null, storageScope: null });
      }
    };

    loadProfile();

    return () => {
      cancelled = true;
    };
  }, [attempt]);

  if (state.status === "loading") {
    return <LoadingScreen />;
  }

  if (state.status === "error") {
    return <ErrorScreen onRetry={() => setAttempt((value) => value + 1)} />;
  }

  const profile = state.profile;
  const workspace = getPlatformWorkspace(profile);
  const isPlatformPath = location.pathname.startsWith("/platform");

  if (workspace === "onprem-unavailable") {
    return <OnPremUnavailableScreen onRetry={() => setAttempt((value) => value + 1)} />;
  }

  if ((workspace === "onprem" || workspace === "managed") && !isPlatformPath) {
    return <Navigate to="/platform" replace />;
  }

  if (
    (workspace === "onprem" || workspace === "managed") &&
    !isPathAllowedForPlatformWorkspace(workspace, location.pathname)
  ) {
    return <Navigate to="/platform" replace />;
  }

  if (workspace === "tenant" && isPlatformPath) {
    return <Navigate to="/chatwindow" replace />;
  }

  if (workspace === "onprem" || workspace === "managed") {
    return (
      <SuperAdminLayout
        sessionProfile={profile}
        storageScope={state.storageScope}
        workspace={workspace}
      />
    );
  }

  return (
    <DashboardLayout
      initialProfile={profile}
      storageScope={state.storageScope}
    />
  );
}
